import {
	Box,
	Button,
	Grid,
	IconButton,
	Typography,
	useMediaQuery,
	useTheme,
	Dialog,
	DialogContent,
	DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FipDiscoveredAccountsSection from './FipDiscoveredAccountsSection';
import WhyShare from '../Sections/WhyShare';
import linkedAccountsSectionStyles from '../Accounts/LinkedAccountsSection.styles';
import discoveryStyles from './AccountDiscoveryContainer.styles';
import { useState, useEffect, useContext } from 'react';
import FipNotDiscoveredAccountsSection from './FipNotDiscoveredAccountsSection';
import { isAccountLinked, getUniqueKey } from '../../utils/helper.utils';
import {
	isAllMandatoryAccountsLinked,
	filterSelectedAccountsByRestrictions,
} from '../../utils/targetedAccounts.utils';
import StepperContext from '../Context/StepperContext';
import { useTranslation } from '../../hooks/useTranslation';
import { toast } from 'react-toastify';

function AccountDiscoveryContainer(props) {
	const { t } = useTranslation();
	const { nextStep } = useContext(StepperContext);
	const theme = useTheme();
	const mobileScreen = useMediaQuery(theme.breakpoints.down('md'));

	// Check for dark theme
	const isDark =
		props.customDetails?.darkTheme === true || theme.palette.mode === 'dark';
	const [changingMobileNumber, setChangingMobileNumber] = useState(false);
	const [openDialog, setOpenDialog] = useState(false);
	const [accountsNotLinked, setAccountsNotLinked] = useState([]);
	const [accountsNotLinkedLength, setAccountsNotLinkedLength] = useState(0);
	const [selectedAccountLinkedLength, setSelectedAccountLinkedLength] =
		useState(0);
	const [allLinkedAccounts, setAllLinkedAccounts] = useState(
		props.accountState.linkedAccountsFull
	);

	// Logic to calculate accounts that are selected but not linked
	useEffect(() => {
		const accountsNotLinkedSet = new Set();
		const filteredSelectedAccounts = global.redirectAccountRestrictions
			? filterSelectedAccountsByRestrictions(
					props.accountState.selectedAccounts
			  )
			: props.accountState.selectedAccounts;

		const unlinkedAccounts = filteredSelectedAccounts.filter((account) => {
			// Create a unique key for each account
			const uniqueKey = getUniqueKey(account, account.fipId);
			if (!accountsNotLinkedSet.has(uniqueKey)) {
				accountsNotLinkedSet.add(uniqueKey);
				return !isAccountLinked(
					account,
					props.accountState.linkedAccountsFull,
					account.fipId
				);
			}
			return false;
		});

		// Set the unlinked accounts to the state
		setAccountsNotLinked(unlinkedAccounts);
		setAccountsNotLinkedLength(unlinkedAccounts.length); // Updates length here
	}, [
		props.accountState.selectedAccounts,
		props.accountState.linkedAccountsFull,
	]);

	const handleContinueClick = () => {
		if (
			global.redirectAccountRestrictions &&
			!isAllMandatoryAccountsLinked(
				props.accountState.discoveredAccounts,
				allLinkedAccounts
			)
		) {
			toast.error(t('accountDiscovery.errors.linkMandatoryAccounts'));
			return;
		}

		if (
			accountsNotLinkedLength > 0 &&
			selectedAccountLinkedLength !==
				props.accountState.discoveredAccounts.length
		) {
			setOpenDialog(true);
		} else {
			props.history.push(`/payment-consent`);
			nextStep(1);
		}
	};
	const handleDialogClose = (proceed) => {
		setOpenDialog(false);
		if (proceed) {
			props.history.push(`/payment-consent`);
			nextStep(1);
		}
	};
	return (
		<>
			<Dialog open={openDialog} onClose={() => handleDialogClose(false)}>
				<DialogContent>
					{t('accountDiscovery.messages.continueDialogheading')}
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => handleDialogClose(false)}
						sx={{
							backgroundColor: isDark
								? 'transparent !important'
								: '#ffffff !important',
							color: '#E94B4B !important',
							borderColor: '#E94B4B !important',
							'&:hover': {
								backgroundColor: 'rgba(233, 75, 75, 0.04) !important',
								borderColor: '#E94B4B !important',
							},
						}}
					>
						{t('common.buttons.cancel')}
					</Button>
					<Button
						onClick={() => handleDialogClose(true)}
						sx={{
							backgroundColor: `${
								props.customDetails?.ctaBackgroundColor ??
								props.customDetails?.primaryColor
							} !important`,
							color: `${
								props.customDetails?.ctaTextColor ||
								(isDark ? '#000000' : '#FFFFFF')
							} !important`,
							border: `1px solid ${
								props?.customDetails?.ctaBorderColor ??
								props?.customDetails?.primaryColor
							}`,
							'&:hover': {
								backgroundColor: `${
									props.customDetails?.ctaHoverPrimaryColor ??
									props.customDetails?.primaryColor
								} !important`,
							},
						}}
					>
						{t('common.buttons.proceed')}
					</Button>
				</DialogActions>
			</Dialog>
			<Box
				sx={{
					...discoveryStyles.container,
					padding: mobileScreen ? '2rem 1.5rem 5.5rem 1.5rem' : '2rem 1.875rem',
					minWidth: mobileScreen ? '100vw' : 'auto',
					backgroundColor: isDark ? '#161616' : theme.palette.primary.light,
					color: isDark ? '#FFFFFF' : '#000000',
					gap: mobileScreen ? '1.25rem' : '1.5rem',
					mb: '4rem',
				}}
			>
				<Box width={'100%'}>
					<Box sx={discoveryStyles.headingContainer}>
						<Box sx={discoveryStyles.heading}>
							{!mobileScreen && global.isAddAccount ? (
								<IconButton
									disableRipple
									sx={{ pl: 0 }}
									onClick={props.handleBack}
								>
									<ArrowBackIcon color="primary" />
								</IconButton>
							) : null}
							<Typography
								sx={{
									...discoveryStyles.headingText,
									color: isDark ? '#FFFFFF' : 'inherit',
								}}
							>
								{t('accountDiscovery.titles.verifyAccounts')}
							</Typography>
						</Box>
						{!mobileScreen ? (
							<Button
								size="large"
								variant="contained"
								onClick={handleContinueClick}
								disabled={props.accountState.isDiscovering}
								sx={{
									...discoveryStyles.nextButton,
									opacity: props.accountState.isDiscovering ? 0.7 : 1,
									position: 'relative',
									'&::after': props.accountState.isDiscovering
										? {
												content: '""',
												position: 'absolute',
												top: 0,
												left: 0,
												right: 0,
												bottom: 0,
												background:
													'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
												backgroundSize: '200% 100%',
												animation: 'loading 2s infinite linear',
												'@keyframes loading': {
													'0%': { backgroundPosition: '200% 0' },
													'100%': { backgroundPosition: '-200% 0' },
												},
										  }
										: {},
								}}
							>
								{props.accountState.isDiscovering
									? t('common.buttons.discovering')
									: t('common.buttons.continue')}
							</Button>
						) : null}
					</Box>
					<Typography
						color={isDark ? '#7A8185' : theme.palette.black60}
						sx={discoveryStyles.subTitleText}
					>
						{t('accountDiscovery.messages.linkAccountsWithOtp')}
						<span
							style={{
								color: isDark ? '#FFFFFF' : theme.palette.info.main,
								fontWeight: '700',
							}}
						>
							{' '}
							{props?.accountState?.noDiscoveredAccounts?.phoneNumber
								? props?.accountState?.noDiscoveredAccounts?.phoneNumber
								: global.mobileNumber
								? global.mobileNumber
								: global.mobileNumberArray[0]}{' '}
						</span>
						{/* {global.sdkOptions.allowDifferentMobile==="Y"&&
            <Link
              color={theme.palette.primary.main}
              onClick={()=>{ props.history.push(`/NoAccountFound/${global.mobileNumber}?step=${101}&fip=${props.match.params.fid}`) }}
              sx={discoveryStyles.changeNumberLink}
            >
              CHANGE MOBILE  .<br />
            </Link>}*/}
					</Typography>
				</Box>
				{!mobileScreen && (
					<Box
						sx={{
							position: 'fixed',
							bottom: 0,
							left: 0,
							right: 0,
							backgroundColor: isDark ? '#161616' : 'white',
							padding: '2rem',
							zIndex: 6,
						}}
					>
						<WhyShare {...props} />
					</Box>
				)}
				<Grid container sx={discoveryStyles.discoveredAccountsContainer}>
					{props.accountState.discoveredAccounts.map((accountsSingleFip) => {
						return (
							<FipDiscoveredAccountsSection
								key={accountsSingleFip.fipId}
								changingMobileNumber={changingMobileNumber}
								setChangingMobileNumber={setChangingMobileNumber}
								accountsSingleFip={accountsSingleFip}
								accountsNotLinkedLength={accountsNotLinkedLength}
								setAccountsNotLinkedLength={setAccountsNotLinkedLength}
								selectedAccountLinkedLength={selectedAccountLinkedLength}
								setSelectedAccountLinkedLength={setSelectedAccountLinkedLength}
								allLinkedAccounts={allLinkedAccounts}
								setAllLinkedAccounts={setAllLinkedAccounts}
								{...props}
							/>
						);
					})}
					{!props.accountState.isDiscovering &&
						props.accountState.noDiscoveredAccounts.fips.length > 0 && (
							<FipNotDiscoveredAccountsSection
								changingMobileNumber={changingMobileNumber}
								setChangingMobileNumber={setChangingMobileNumber}
								{...props}
							/>
						)}
				</Grid>
			</Box>

			{mobileScreen ? (
				<Box
					sx={{
						...linkedAccountsSectionStyles.buttonGroup,
						bottom: mobileScreen ? '1px' : '1.875rem',
						backgroundColor: mobileScreen
							? isDark
								? '#161616'
								: 'white'
							: 'none',
						color: mobileScreen ? (isDark ? '#FFFFFF' : '#000000') : 'inherit',
						gap: mobileScreen ? '1rem' : '1.25rem',
						width: mobileScreen ? '100vw' : '20%',
					}}
				>
					{global.isAddAccount ? (
						<Box
							sx={{
								bottom: mobileScreen ? '0rem' : '1.875rem',
								right: mobileScreen ? '0rem' : '0rem',
								backgroundColor: mobileScreen ? 'white' : 'none',
								gap: mobileScreen ? '1rem' : '1.25rem',
								width: mobileScreen ? '100vw' : '20%',
								flexWrap: 'wrap',
								...linkedAccountsSectionStyles.buttonGroup,
							}}
						>
							<Button
								variant="outlined"
								fullWidth={mobileScreen}
								color="primary"
								onClick={() => props.handleBack()}
								sx={{ flex: 1, background: theme.palette.primary.white }}
							>
								{t('common.buttons.back')}
							</Button>
							<Button
								variant="contained"
								fullWidth={mobileScreen}
								onClick={handleContinueClick}
								disabled={props.accountState.isDiscovering}
								sx={{
									backgroundColor:
										props?.customDetails?.ctaBackgroundColor ??
										props.customDetails.primaryColor,
									flex: 1,
									border: `1px solid ${
										props?.customDetails?.ctaBorderColor ??
										props?.customDetails?.primaryColor
									}`,
									'&:hover': {
										backgroundColor:
											props?.customDetails?.ctaHoverPrimaryColor ??
											props?.customDetails?.primaryColor,
										border: props?.customDetails?.ctaHoverBorderColor
											? `1px solid ${props?.customDetails?.ctaHoverBorderColor}`
											: `1px solid ${props?.customDetails?.primaryColor}`,
									},
									'&.Mui-disabled': {
										border: props?.customDetails?.ctaDisableBorderColor
											? `1px solid ${props?.customDetails?.ctaDisableBorderColor}`
											: '0', // or any style you want
									},
									opacity: props.accountState.isDiscovering ? 0.7 : 1,
									position: 'relative',
									'&::after': props.accountState.isDiscovering
										? {
												content: '""',
												position: 'absolute',
												top: 0,
												left: 0,
												right: 0,
												bottom: 0,
												background:
													'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
												backgroundSize: '200% 100%',
												animation: 'loading 2s infinite linear',
												'@keyframes loading': {
													'0%': { backgroundPosition: '200% 0' },
													'100%': { backgroundPosition: '-200% 0' },
												},
										  }
										: {},
								}}
							>
								{props.accountState.isDiscovering
									? t('common.buttons.discovering')
									: t('common.buttons.continue')}
							</Button>
							<WhyShare {...props} />
						</Box>
					) : null}
					<Box
						sx={{
							bottom: mobileScreen ? '0rem' : '1.875rem',
							right: mobileScreen ? '0rem' : '0rem',
							backgroundColor: isDark
								? mobileScreen
									? '#000000'
									: 'none'
								: 'white',
							gap: mobileScreen ? '1rem' : '1.25rem',
							width: mobileScreen ? '100vw' : '20%',
							flexWrap: 'wrap',
							...linkedAccountsSectionStyles.buttonGroup,
						}}
					>
						<Button
							variant="contained"
							fullWidth={mobileScreen}
							onClick={handleContinueClick}
							disabled={props.accountState.isDiscovering}
							sx={{
								backgroundColor: props?.customDetails?.primaryColor,
								flex: 1,
								opacity: props.accountState.isDiscovering ? 0.7 : 1,
								position: 'relative',
								'&::after': props.accountState.isDiscovering
									? {
											content: '""',
											position: 'absolute',
											top: 0,
											left: 0,
											right: 0,
											bottom: 0,
											background: `linear-gradient(90deg, transparent 0%, ${
												isDark ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.3)'
											} 50%, transparent 100%)`,
											backgroundSize: '200% 100%',
											animation: 'loading 2s infinite linear',
											'@keyframes loading': {
												'0%': { backgroundPosition: '200% 0' },
												'100%': { backgroundPosition: '-200% 0' },
											},
									  }
									: {},
							}}
						>
							{props.accountState.isDiscovering
								? t('common.buttons.discovering')
								: t('common.buttons.continue')}
						</Button>
						<WhyShare {...props} />
					</Box>
				</Box>
			) : null}
		</>
	);
}

export default AccountDiscoveryContainer;
